import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { api, type ApiResponse } from "@/api/client";
import type { User } from "@ckgo2/shared/types";
import { formatDate } from "@/lib/format";
import type { Communication } from "@ckgo2/shared/types";
import {
  Mail,
  Phone,
  MessageSquare,
  FileText,
  CreditCard,
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Send,
  ArrowDownLeft,
  ArrowUpRight,
  Loader2,
} from "lucide-react";

interface CommunicationTimelineProps {
  /** Exactly one of orderId / leadId — the entity this timeline belongs to. */
  orderId?: string;
  leadId?: string;
  customerId: string;
  communications: Communication[];
  /** When set (leads), enables a real outbound reply (sends + threads via /inbox/reply). */
  contactEmail?: string;
  /** A draft to drop into the reply box (e.g. the AI first-reply on a lead). Opens the composer. */
  prefillReply?: string;
}

const TYPE_CONFIG: Record<string, { icon: typeof Mail; label: string; color: string }> = {
  email: {
    icon: Mail,
    label: i18n.t("orders.timeline.types.email"),
    color: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  },
  phone: {
    icon: Phone,
    label: i18n.t("orders.timeline.types.phone"),
    color: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
  },
  note: {
    icon: MessageSquare,
    label: i18n.t("orders.timeline.types.note"),
    color: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  },
  sms: {
    icon: MessageSquare,
    label: i18n.t("orders.timeline.types.sms"),
    color: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
  },
  status_change: {
    icon: RefreshCw,
    label: i18n.t("orders.timeline.types.statusChange"),
    color: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  },
  payment: {
    icon: CreditCard,
    label: i18n.t("orders.timeline.types.payment"),
    color: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  document: {
    icon: FileText,
    label: i18n.t("orders.timeline.types.document"),
    color: "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400",
  },
};

export default function CommunicationTimeline({
  orderId,
  leadId,
  customerId: _customerId,
  communications,
  contactEmail,
  prefillReply,
}: CommunicationTimelineProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteType, setNoteType] = useState<"note" | "phone" | "email">("note");
  const [noteSubject, setNoteSubject] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // @mention autocomplete — active users, filtered by the @token being typed.
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<ApiResponse<User[]>>("/api/admin/users"),
  });
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const mentionMatches =
    mentionQuery === null
      ? []
      : (usersData?.data ?? [])
          .filter((u) => u.isActive !== false)
          .filter((u) => (u.name || "").toLowerCase().startsWith(mentionQuery.toLowerCase()))
          .slice(0, 6);

  const onNoteChange = (value: string) => {
    setNoteContent(value);
    // Detect a trailing "@token" (assumes caret at end — the common case).
    const m = value.match(/(?:^|\s)@([\p{L}][\p{L}-]*)?$/u);
    setMentionQuery(m ? (m[1] ?? "") : null);
  };

  const insertMention = (name: string) => {
    const handle = (name || "").trim().split(/\s+/)[0]; // first name = the handle
    setNoteContent((prev) => prev.replace(/@([\p{L}][\p{L}-]*)?$/u, `@${handle} `));
    setMentionQuery(null);
  };

  // Works for both orders and leads — the post path + cache key follow the entity.
  const postPath = orderId
    ? `/api/admin/orders/${orderId}/communications`
    : `/api/admin/leads/${leadId}/communications`;
  const invalidateKey = orderId ? ["order", orderId] : ["lead", leadId];

  const addNoteMutation = useMutation({
    mutationFn: async (data: {
      type: string;
      subject: string;
      content: string;
      direction: string;
    }) => {
      return api.post(postPath, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invalidateKey });
      setIsAddingNote(false);
      setNoteSubject("");
      setNoteContent("");
    },
  });

  // Real outbound reply (vision §5.1 / E2E-001): leads send via the connected mailbox and thread
  // into the conversation. Orders (no contactEmail passed) keep the log-only "Add Entry → email".
  const canReply = !!(leadId && contactEmail);
  const replyThreadId = communications.find((c) => c.threadId)?.threadId ?? null;
  const lastInboundSubject = communications.find(
    (c) => c.direction === "inbound" && c.subject,
  )?.subject;
  const [isReplying, setIsReplying] = useState(false);
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const replyMutation = useMutation({
    mutationFn: (data: { subject: string; body: string }) =>
      api.post("/api/admin/inbox/reply", {
        to: contactEmail,
        subject: data.subject,
        body: data.body,
        leadId,
        threadId: replyThreadId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invalidateKey });
      setIsReplying(false);
      setReplySubject("");
      setReplyBody("");
    },
  });
  const openReply = () => {
    setReplySubject(
      lastInboundSubject
        ? `Re: ${lastInboundSubject.replace(/^re:\s*/i, "")}`
        : t("orders.timeline.defaultReplySubject"),
    );
    setIsReplying(true);
  };
  // "Use as reply" on the AI draft card → open the composer pre-filled (garant edits/sends).
  useEffect(() => {
    if (!prefillReply || !canReply) return;
    setReplyBody(prefillReply);
    setReplySubject(
      (s) =>
        s ||
        (lastInboundSubject
          ? `Re: ${lastInboundSubject.replace(/^re:\s*/i, "")}`
          : t("orders.timeline.defaultReplySubject")),
    );
    setIsReplying(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillReply]);
  const handleReply = () => {
    if (!replyBody.trim()) return;
    replyMutation.mutate({
      subject: replySubject || t("orders.timeline.defaultReplySubject"),
      body: replyBody,
    });
  };

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const handleAddNote = () => {
    if (!noteContent.trim()) return;

    addNoteMutation.mutate({
      type: noteType,
      subject:
        noteSubject ||
        (noteType === "note" ? t("orders.timeline.internalNoteSubject") : noteSubject),
      content: noteContent,
      direction: noteType === "email" ? "outbound" : "internal",
    });
  };

  return (
    <div className="space-y-4">
      {/* Header with add button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("orders.timeline.title")}
        </h3>
        <div className="flex items-center gap-2">
          {canReply && (
            <button type="button" onClick={openReply} className="btn-primary btn-sm">
              <Send className="h-4 w-4" />
              {t("orders.timeline.reply")}
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsAddingNote(!isAddingNote)}
            className="btn-ghost btn-sm"
          >
            <MessageSquare className="h-4 w-4" />
            {t("orders.timeline.addEntry")}
          </button>
        </div>
      </div>

      {/* Real reply composer (leads) — sends via /inbox/reply and threads the conversation. */}
      {isReplying && canReply && (
        <div className="border-primary-200 bg-primary-50/50 dark:border-primary-800 dark:bg-primary-900/10 rounded-lg border p-4">
          <p className="mb-2 text-xs text-gray-500">
            {t("orders.timeline.replyToPrefix")} <span className="font-medium">{contactEmail}</span>{" "}
            {replyThreadId
              ? t("orders.timeline.replyViaMailboxThreaded")
              : t("orders.timeline.replyViaMailbox")}
          </p>
          <input
            type="text"
            value={replySubject}
            onChange={(e) => setReplySubject(e.target.value)}
            placeholder={t("orders.timeline.subjectPlaceholder")}
            className="input mb-2"
          />
          <textarea
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            placeholder={t("orders.timeline.replyPlaceholder")}
            rows={4}
            className="input resize-none"
          />
          {replyMutation.isError && (
            <p className="mt-2 text-xs text-red-600">
              {(replyMutation.error as Error)?.message || t("orders.timeline.sendFailed")}
            </p>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setIsReplying(false)} className="btn-ghost btn-sm">
              {t("common.actions.cancel")}
            </button>
            <button
              type="button"
              onClick={handleReply}
              disabled={!replyBody.trim() || replyMutation.isPending}
              className="btn-primary btn-sm"
            >
              {replyMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {t("orders.timeline.sendReply")}
            </button>
          </div>
        </div>
      )}

      {/* Add note form */}
      {isAddingNote && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
          <div className="mb-4 flex gap-2">
            {(canReply ? (["note", "phone"] as const) : (["note", "phone", "email"] as const)).map(
              (type) => {
                const config = TYPE_CONFIG[type];
                const Icon = config.icon;
                return (
                  <button
                    type="button"
                    key={type}
                    onClick={() => setNoteType(type)}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      noteType === type
                        ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {config.label}
                  </button>
                );
              },
            )}
          </div>

          <div className="space-y-3">
            {noteType !== "note" && (
              <input
                type="text"
                placeholder={t("orders.timeline.subjectInputPlaceholder")}
                value={noteSubject}
                onChange={(e) => setNoteSubject(e.target.value)}
                className="input"
              />
            )}
            <div className="relative">
              <textarea
                placeholder={
                  noteType === "note"
                    ? t("orders.timeline.notePlaceholder")
                    : noteType === "phone"
                      ? t("orders.timeline.phonePlaceholder")
                      : t("orders.timeline.emailPlaceholder")
                }
                value={noteContent}
                onChange={(e) => onNoteChange(e.target.value)}
                rows={3}
                className="input resize-none"
              />
              {mentionMatches.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  {mentionMatches.map((u) => (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => insertMention(u.name)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <span className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium">
                        {u.name.charAt(0).toUpperCase()}
                      </span>
                      <span className="text-gray-900 dark:text-white">{u.name}</span>
                      <span className="text-xs text-gray-400">
                        @{u.name.trim().split(/\s+/)[0]}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsAddingNote(false);
                  setNoteSubject("");
                  setNoteContent("");
                }}
                className="btn-ghost btn-sm"
              >
                {t("common.actions.cancel")}
              </button>
              <button
                type="button"
                onClick={handleAddNote}
                disabled={!noteContent.trim() || addNoteMutation.isPending}
                className="btn-primary btn-sm"
              >
                {addNoteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {t("common.actions.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      {communications.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-500 dark:border-gray-600">
          <Clock className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p>{t("orders.timeline.empty")}</p>
        </div>
      ) : (
        <div className="relative space-y-0">
          {/* Timeline line */}
          <div className="absolute left-5 top-0 h-full w-0.5 bg-gray-200 dark:bg-gray-700" />

          {communications.map((comm, _index) => {
            const config = TYPE_CONFIG[comm.type] || TYPE_CONFIG.note;
            const Icon = config.icon;
            const isExpanded = expandedItems.has(comm.id);
            const hasLongContent = comm.content && comm.content.length > 150;

            return (
              <div key={comm.id} className="relative flex gap-4 pb-6">
                {/* Icon */}
                <div
                  className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${config.color}`}
                >
                  <Icon className="h-5 w-5" />
                </div>

                {/* Content */}
                <div className="flex-1 pt-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {comm.subject || config.label}
                        </span>
                        {comm.direction === "inbound" && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <ArrowDownLeft className="h-3 w-3" />
                            {t("orders.timeline.inbound")}
                          </span>
                        )}
                        {comm.direction === "outbound" && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <ArrowUpRight className="h-3 w-3" />
                            {t("orders.timeline.outbound")}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {formatDate(comm.createdAt, { hour: "2-digit", minute: "2-digit" })}
                        {comm.createdBy && ` • ${comm.createdBy}`}
                      </p>
                    </div>
                  </div>

                  {comm.content && (
                    <div className="mt-2">
                      <p
                        className={`text-sm text-gray-600 dark:text-gray-400 ${
                          !isExpanded && hasLongContent ? "line-clamp-2" : ""
                        }`}
                      >
                        {comm.content}
                      </p>
                      {hasLongContent && (
                        <button
                          type="button"
                          onClick={() => toggleExpand(comm.id)}
                          className="text-primary-600 hover:text-primary-700 mt-1 flex items-center gap-1 text-xs"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="h-3 w-3" />
                              {t("orders.timeline.less")}
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-3 w-3" />
                              {t("orders.timeline.more")}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
